import errorReporter from '@app/errorReporter';
import { Color, Flex, List, Text } from '@bb/elements-web';
import { Button } from 'components';
import React from 'react';
import styled from 'styled-components';
import searchNonAgents, {
  NonAgentSearchUser,
} from '../../../../../api/NonAgentController/search';
import SelectAgentInput from '../../components/SelectAgentInput';

// Copied from AgentRosterInput from assets/app/react-common
// but removed agent + custom agent logic and things not applicable to physical showings
// and changed the render to not rely on angular styles (which in turn required some logical changes)

type InPersonRosterInputState = {
  loading: boolean;
  /** debounce variable */
  cancelSearch: boolean;
  /** Search results from using the enter key (results in a pagination render state) */
  searchAgentUserResults?: NonAgentSearchUser[];
  /** Search results from typing in input */
  searchUserPopups: NonAgentSearchUser[];
  agentSuggestName: string;
  /** If arrow keys are used to navigate dropdown, then disable enter key functionality */
  usedArrowKeys: boolean;
};

type InPersonRosterInputProps = {
  selectUser: (item: NonAgentSearchUser) => void;
  showCustomContactEnter: boolean;
  onCustomContactEnter: () => void;
  deselectUser: () => void;
};

class InPersonRosterInput extends React.Component<
  InPersonRosterInputProps,
  InPersonRosterInputState
> {
  constructor(props: InPersonRosterInputProps) {
    super(props);
    this.state = {
      loading: false,
      cancelSearch: false,
      searchAgentUserResults: undefined,
      searchUserPopups: [],
      agentSuggestName: '',
      usedArrowKeys: false,
    };
  }

  _handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { agentSuggestName, usedArrowKeys } = this.state;
    if (event.key === 'Enter') {
      if (!agentSuggestName || usedArrowKeys) return;
      this.search(agentSuggestName);
    } else if (['Escape', 'Esc'].includes(event.key)) {
      this.setState({ searchUserPopups: [], usedArrowKeys: false });
    } else if (['ArrowUp', 'Up', 'ArrowDown', 'Down'].includes(event.key)) {
      this.setState({ usedArrowKeys: true });
    }
  };

  public select = (user: NonAgentSearchUser) => {
    const { selectUser } = this.props;

    this.setState(
      {
        searchAgentUserResults: undefined,
        usedArrowKeys: false,
        cancelSearch: false,
      },
      () => selectUser(user),
    );
  };

  public fetchUsers = async (name: string) => {
    const users = await searchNonAgents({
      name,
    });
    return { users };
  };

  public search = async (name: string) => {
    try {
      const { deselectUser } = this.props;
      this.setState({
        loading: true,
        cancelSearch: true,
        searchUserPopups: [],
      });
      deselectUser();

      const { users } = await this.fetchUsers(name);

      this.setState({
        searchAgentUserResults: users.sort((a, b) => b.score - a.score),
        cancelSearch: false,
      });
    } catch (err) {
      errorReporter.error(`Error searching roster: ${err}`);
    } finally {
      this.setState({ loading: false });
    }
  };

  public agentSuggest = async (name: string) => {
    const { cancelSearch, agentSuggestName } = this.state;

    if (agentSuggestName !== name) return;
    if (!name) {
      this.setState({ searchUserPopups: [] });
      return;
    }
    if (cancelSearch) {
      this.setState({ searchUserPopups: [], cancelSearch: false });
      return;
    }
    try {
      const { users } = await this.fetchUsers(name);
      const searchUserPopups = users
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
      this.setState({
        searchUserPopups,
      });
    } catch (err) {
      errorReporter.error(`Error searching roster: ${err}`);
    } finally {
      this.setState({ cancelSearch: false });
    }
  };

  public onSearchChange = (agentSuggestName: string) => {
    this.setState(
      {
        agentSuggestName,
        searchAgentUserResults: undefined,
      },
      () => {
        setTimeout(() => {
          this.agentSuggest(agentSuggestName);
        }, 300);
      },
    );
  };

  public onAgentSelect = (userIndex: number) => {
    const user = this.state.searchUserPopups[userIndex];
    this.setState(
      {
        cancelSearch: true,
        agentSuggestName: '',
        searchUserPopups: [],
      },
      () => this.select(user),
    );
  };

  render() {
    const { onCustomContactEnter, showCustomContactEnter } = this.props;
    const { agentSuggestName, searchAgentUserResults, searchUserPopups } =
      this.state;

    return (
      <StyledFlex vertical gap="small">
        <Flex justify="space-between">
          <Text thickness="bold">Search Name</Text>
          {showCustomContactEnter && (
            <Button
              size="small"
              type="text"
              data-cy="inPersonNonAgent-customContact-btn"
              onClick={onCustomContactEnter}
            >
              Enter Custom Contact
            </Button>
          )}
        </Flex>
        <SelectAgentInput
          searchValue={agentSuggestName}
          onKeyDown={this._handleKeyDown}
          onChange={this.onAgentSelect}
          onSearch={this.onSearchChange}
          id="agent-roster-input"
          dataCy="agent-roster-input"
          options={searchUserPopups.map((user, index) => ({
            value: index,
            label: (
              <Flex vertical data-cy={user.id} key={user.id}>
                <Text thickness="bold" dataCy={`${user.id}-name`}>
                  {user.firstName} {user.lastName}
                </Text>
                <Text dataCy={`${user.id}-company`}>{user.companyName}</Text>
                <Text dataCy={`${user.id}-${user.type}`}>{user.type}</Text>
              </Flex>
            ),
          }))}
        />

        {searchAgentUserResults && (
          <List
            dataCy="inPersonNonAgent-list"
            dataSource={searchAgentUserResults}
            locale={{
              emptyText: 'No users found',
            }}
            renderItem={user => (
              <StyledListItem vertical data-cy={user.id} key={user.id}>
                <Text thickness="bold" dataCy={`${user.id}-name`}>
                  {user.firstName} {user.lastName}
                </Text>
                <Text dataCy={`${user.id}-company`}>{user.companyName}</Text>
                <Text dataCy={`${user.id}-${user.type}`}>{user.type}</Text>
              </StyledListItem>
            )}
          />
        )}
      </StyledFlex>
    );
  }
}

const StyledFlex = styled(Flex)`
  margin-top: 16px;
`;

const StyledListItem = styled(Flex)`
  cursor: pointer;
  padding: 4px 16px;
  &:hover {
    background-color: ${Color.LIGHTER_GREY};
  }
`;

export default InPersonRosterInput;
